import React, { useEffect, useRef, useState, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";

import { db } from "../../../firebase";
import { doc, getDoc, setDoc } from "firebase/firestore";

import { useUser } from '../../../store/UserProvider';
import { saveUserLog } from '../LogandUtils'; // 경로에 맞게 수정

import SEO from '../../SEO';
import Popup from "../../mobile/Popup";
import Loading from "../../Loading";
import MyPageMenu from "./MyPageMenu";
import KakaoMapSearch from "../KakaoMapSearch";

import styleClasses from '../css/mobile/MyPage.module.scss';

function ContactDetail() {
  const navigate = useNavigate();

  const { contactId } = useParams();

  const { user, userLoading } = useUser();

  const nameRef         = useRef();
  const emailRef        = useRef();
  const isSubmitting    = useRef(false);

  const [openMenu, setOpenMenu] = useState(false);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState('');
  const [userID, setUserID]     = useState(null);

  const [nameError, setNameError]           = useState('');
  const [nameErrorText, setNameErrorText]   = useState('');
  const [emailError, setEmailError]         = useState('');
  const [emailErrorText, setEmailErrorText] = useState('');
  const [buttonLoading, setButtonLoading]   = useState(false);

  const [openPopup, setOpenPopup]             = useState(false);
  const [popupTitle, setPopupTitle]           = useState('');
  const [popupButtonText, setPopupButtonText] = useState('');
  const [customFunction, setCustomFunction]   = useState('');
  // 초기 연락처 데이터: location을 빈 배열로 설정
  const [contactData, setContactData]         = useState({
    name: "",
    introduction: "",
    location: [], // 변경: 객체({})에서 배열([])로
    email: "",
    sns: {}
  });

  // SNS 추가를 위한 state
  const [newSNSPlatform, setNewSNSPlatform] = useState("");
  // 사용 가능한 SNS 플랫폼 목록
  const availableSNSPlatforms = ["Instagram", "YouTube", "X", "TikTok", "Facebook", "Thread", "LinkedIn", "Naver Blog", "Kakao Opentalk"];

  // 🔹 메뉴 열기
  const toogleMenu = useCallback(
    (event) => {
      const menuIcon = event.target.closest(`.${styleClasses.menuIcon}`);
      const menuBox = event.target.closest(`.${styleClasses.leftContent}`);

      if (openMenu) {
        if (!menuBox) setOpenMenu(false);
      } else {
        if (menuIcon || menuBox) setOpenMenu(true);
      }
    },
    [openMenu, setOpenMenu]
  );

  // 🔹 메뉴 열기 (스크린 전체 이벤트 추가)
  useEffect(() => {
    document.body.addEventListener("click", toogleMenu);

    return () => {
      document.body.removeEventListener("click", toogleMenu);
    };
  }, [toogleMenu]);

  // 🔹 연락처 조회
  const fetchContact = useCallback(async () => {
    try {
      const contactRef = doc(db, "users_data", userID, "contact", contactId);
      const contactSnap = await getDoc(contactRef);
      if (contactSnap.exists()) {
        const data = contactSnap.data();
        // 기존에 저장된 location이 배열이 아니라면 배열로 변환
        if (data.location && !Array.isArray(data.location)) {
          data.location = [data.location];
        }
        // sns 필드가 없으면 기본값 {}로 설정
        setContactData({
            ...data,
            sns: data.sns || {}
        });
      } else {
        setError("연락처 데이터를 찾을 수 없습니다.");
      }
    } catch (err) {
      console.error("연락처 데이터 불러오기 오류:", err);
      setError("연락처 데이터를 불러오는 중 오류가 발생했습니다.");
    }
    setLoading(false);
  }, [contactId, userID]);

  // 🔹 로그인 확인  
  useEffect(() => {
    if (userLoading) return;
  
    if (user?.userID) {
      setUserID(user.userID);
    } else {
      navigate('/login');
    }
  }, [navigate, user?.userID, userLoading]);

  // 🔹 로그인 확인 후 연락처 조회
  useEffect(() => {
    if (!userID) return;

    if (contactId !== "new") {
      fetchContact();
    } else {
      setLoading(false);
    }
  }, [contactId, fetchContact, userID]);

  // 기본 입력값 변경 핸들러
  const handleChange = (e) => {
    const { name, value } = e.target;
    setContactData((prev) => ({ ...prev, [name]: value }));

    if (e.target.id === 'name') {
      setNameError('');
      setNameErrorText('');
    } else if (e.target.id === 'email') {
      setEmailError('');
      setEmailErrorText('');
    }
  };

  // 카카오맵 검색에서 선택한 위치를 동적 필드에 추가하는 함수
  const handleAddLocationFromMap = (selectedPosition) => {
    // selectedPosition: { placeName, lat, lng }
    setContactData((prev) => ({
      ...prev,
      location: [
        ...prev.location,
        {
          placeName: selectedPosition.placeName,
          lat: selectedPosition.lat,
          lng: selectedPosition.lng,
        },
      ],
    }));
  };

  // 동적 필드에서 위치 삭제
  const handleRemoveLocation = (indexToRemove) => {
    setContactData((prev) => ({
      ...prev,
      location: prev.location.filter((_, index) => index !== indexToRemove),
    }));
  };

  // 뮤직링크 값 변경 핸들러 (동적 필드)
  const handleSNSChange = (platform, value) => {
    setContactData((prev) => ({
      ...prev,
      sns: {
        ...prev.sns,
        [platform.toLowerCase()]: value,
      },
    }));
  };

  const handleAddSNS = () => {
    if (newSNSPlatform.trim() === "" || contactData.sns[newSNSPlatform.toLowerCase()]) return;
    setContactData((prev) => ({
      ...prev,
      sns: { ...prev.sns, [newSNSPlatform.toLowerCase()]: "" },
    }));
    setNewSNSPlatform("");
  };

  const handleRemoveSNS = (platform) => {
    setContactData((prev) => {
      const updated = { ...prev.sns };
      delete updated[platform];
      return { ...prev, sns: updated };
    });
  };

  // 현재 추가된 뮤직링크 플랫폼 및 추가 가능한 플랫폼 계산
  const currentSNSPlatforms = Object.keys(contactData.sns);
  const remainingSNSPlatforms = availableSNSPlatforms.filter(
    (platform) => !currentSNSPlatforms.includes(platform.toLowerCase())
  );

  // 이메일 유효성 검사
  const validateEmail = (email) => {
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return emailRegex.test(email);
  };

  // 저장 핸들러
  const handleSave = async (e) => {
    e.preventDefault();

    if (!contactData.name.trim()) {
      setNameError('error');
      setNameErrorText('이름을 입력하세요.');
      nameRef.current.focus();

      setOpenPopup(true);
      setPopupTitle("이름을 입력하세요.");
      setPopupButtonText('');
      setCustomFunction('');
      return false;
    } else if (contactData.email.trim() !== '' && validateEmail(contactData.email) === false) {
      setEmailError('error');
      setEmailErrorText('올바른 이메일 주소를 입력하세요.');
      emailRef.current.focus();

      setOpenPopup(true);
      setPopupTitle("올바른 이메일 주소를 입력하세요.");
      setPopupButtonText('');
      setCustomFunction('');
      return false;
    }

    try {
      if (isSubmitting.current) return; // 실행 중일 경우 중복 클릭 방지
      isSubmitting.current = true;      // 버튼 비활성화
      setButtonLoading(true);           // 버튼 로딩 표시

      // 신규 추가 시 contactData.name 을 ID로 사용, 수정 시 기존 contactId 사용
      const id = contactId === "new" ? contactData.name : contactId;
      const contactRef = doc(db, "users_data", userID, "contact", id);
      await setDoc(contactRef, contactData);
  
      // act 필드가 존재하면, 이미 뮤지카드로 내보낸 상태이므로 해당 뮤지카드의 연락처 정보 업데이트
      if (contactData.act && typeof contactData.act === "object") {
        for (const musicardId in contactData.act) {
          // 1. personal 데이터 업데이트: 이름, 이메일, 뮤직링크 등
          const personalData = {
            name: contactData.name,
            email: contactData.email,
            introduction: contactData.introduction,
            sns: contactData.sns,
          };
          const musicianPersonalRef = doc(
            db,
            `musicians/${musicardId}/contact`,
            "personal"
          );
          await setDoc(musicianPersonalRef, personalData);
          await saveUserLog(
            "musicard_logs",
            musicardId,
            "update",
            musicianPersonalRef.path,
            "ContactDetail"
          );
  
          // 2. location 데이터 업데이트 (연락처의 위치 정보가 배열 형태일 경우)
          if (contactData.location && Array.isArray(contactData.location)) {
            const locationObj = {};
            contactData.location.forEach((loc, index) => {
              locationObj[`${index + 1}`] = loc;
            });
            const musicianLocationRef = doc(
              db,
              `musicians/${musicardId}/contact`,
              "location"
            );
            await setDoc(musicianLocationRef, locationObj);
            await saveUserLog(
              "musicard_logs",
              musicardId,
              "update",
              musicianLocationRef.path,
              "ContactDetail"
            );
          }
        }
      }

      isSubmitting.current = false;   // 버튼 활성화
      setButtonLoading(false);        // 버튼 로딩 숨김

      setOpenPopup(true);
      setPopupTitle('연락처 정보가 저장되었습니다.');
      setPopupButtonText('목록으로 이동');
      setCustomFunction(() => () => navigate("/my-page/contact"));

      if (contactId === "new") {
        setContactData({
          name: "",
          introduction: "",
          location: [], // 변경: 객체({})에서 배열([])로
          email: "",
          sns: {}
        });

        let keyword = document.getElementById('keyword');
        if (keyword) keyword.value = '';
      }
    } catch (err) {
      console.error("연락처 저장 오류:", err);

      isSubmitting.current = false;   // 버튼 활성화
      setButtonLoading(false);        // 버튼 로딩 숨김

      setOpenPopup(true);
      setPopupTitle('연락처 저장 중 오류가 발생했습니다.');
      setPopupButtonText('목록으로 이동');
      setCustomFunction(() => () => navigate("/my-page/contact"));
    }
  };

  return (
    <>
      <SEO title={`연락처 ${ contactId === 'new' ? '추가' : '수정' }`} />
      <div className={styleClasses.myPageMobile}>
        <div className={styleClasses.inner}>
          <div className={styleClasses.title}>
            <h1>
              Contacts {contactId === "new" ? "(새 연락처 추가)" : ""}
            </h1>
            <button className={ styleClasses.menuIcon }>
              <div className="commonIcon mobile">
                <i className={`${ openMenu ? 'upWhite' : 'downWhite' } w24`}></i>
              </div>
            </button>
          </div>
          <div className={styleClasses.editContent}>
            <div className={`${ styleClasses.leftContent } ${ openMenu ? styleClasses.open : '' }`}>
              <MyPageMenu currentPage='contact' />
            </div>
            <div className={styleClasses.rightContent}>
              <div className={`commonForm ${ styleClasses.contactDetail }`}>
                {
                  loading
                  ?
                  <Loading />                  
                  :
                  error
                  ?
                  <p className={ styleClasses.error }>{ error }</p>
                  :
                  (
                    <>
                    {/* 이름 */}
                    <div className="formRow fullWidth">
                      <div className="labelBox">
                        <label htmlFor="name">이름</label>
                      </div>
                      <div className={`fieldBox ${ nameError }`}>
                        <input type="text" name="name" id="name" ref={ nameRef } value={contactData.name} onChange={handleChange} placeholder="이름" />
                        <div className="fieldError">
                          <p>{ nameErrorText }</p>
                        </div>
                      </div>
                    </div>
                    {/* 소개 */}
                    <div className="formRow fullWidth">
                      <div className="labelBox">
                        <label htmlFor="introduction">소개</label>
                        <div className={`commonIcon toolTip mobile ${ styleClasses.topButtonToolTip }`}>
                          <i className="questionBlack w16"></i>
                          <div className="toolTipText">
                            <p>나에게 연락하려는 사람들에게 <br />간단 소개를 해보세요!</p>
                          </div>
                        </div>
                      </div>
                      <div className="fieldBox">
                        <textarea name="introduction" id="introduction" value={contactData.introduction} onChange={handleChange} placeholder="소개" />
                      </div>
                    </div>
                    {/* 주 활동지역 - 카카오맵 검색 적용 */}
                    <div className="formRow fullWidth">
                      <div className="labelBox">
                        <label>주 활동지역</label>
                        <div className={`commonIcon toolTip mobile ${ styleClasses.topButtonToolTip }`}>
                          <i className="questionBlack w16"></i>
                          <div className="toolTipText">
                            <p>주로 활동하는 지역(서울 금천구 등) 또는 <br/>작업실이나 스튜디오, 사업장을 입력해보세요.</p>
                          </div>
                        </div>
                      </div>
                      <div className="fieldBox">
                        <KakaoMapSearch
                          mobile={ true }
                          setOpenPopup={ setOpenPopup }
                          setPopupTitle={ setPopupTitle }
                          setPopupButtonText={ setPopupButtonText }
                          setCustomFunction={ setCustomFunction }
                          onSelect={(selectedPosition) => {
                            // selectedPosition: { lat, lng, placeName }
                            handleAddLocationFromMap(selectedPosition);
                          }}
                        />
                        {contactData.location.length > 0 &&
                          contactData.location.map((loc, index) => (
                            <div key={index} className={styleClasses.dynamicRow}>
                              <div className={`formRow ${ styleClasses.customFormRow }`}>
                                <div className="labelBox">
                                  <label htmlFor="selectedPlace">선택한 장소</label>
                                </div>
                                <div className="fieldBox">
                                  <input
                                  type="text"
                                  id="selectedPlace"
                                  value={loc.placeName}
                                  placeholder="장소명"
                                  onChange={(e) => {
                                      const newPlaceName = e.target.value;
                                      setContactData((prev) => {
                                      const updatedLocations = [...prev.location];
                                      updatedLocations[index] = {
                                          ...updatedLocations[index],
                                          placeName: newPlaceName,
                                      };
                                      return { ...prev, location: updatedLocations };
                                      });
                                  }}
                                  />
                                </div>
                              </div>
                              <div className={`formRow ${ styleClasses.customFormRow }`}>
                                <div className="labelBox">
                                  <label htmlFor="selectedLatitude">위도</label>
                                </div>
                                <div className="fieldBox">
                                  <input
                                    type="text"
                                    id="selectedLatitude"
                                    value={loc.lat}
                                    placeholder="위도"
                                    onChange={(e) => {
                                        const newLat = e.target.value;
                                        setContactData((prev) => {
                                        const updatedLats = [...prev.lat];
                                        updatedLats[index] = {
                                            ...updatedLats[index],
                                            lat: newLat,
                                        };
                                        return { ...prev, lat: updatedLats };
                                        });
                                    }}
                                  />
                                </div>
                              </div>
                              <div className={`formRow ${ styleClasses.customFormRow }`}>
                                <div className="labelBox">
                                  <label htmlFor="selectedLongitude">경도</label>
                                </div>
                                <div className="fieldBox">
                                  <input
                                    type="text"
                                    id="selectedLongitude"
                                    value={loc.lng}
                                    placeholder="경도"
                                    onChange={(e) => {
                                      const newLng = e.target.value;
                                      setContactData((prev) => {
                                      const updatedLngs = [...prev.lng];
                                      updatedLngs[index] = {
                                          ...updatedLngs[index],
                                          lng: newLng,
                                      };
                                      return { ...prev, lng: updatedLngs };
                                      });
                                    }}
                                  />
                                </div>
                              </div>
                              <div className={`commonButton ${ styleClasses.buttonInRow}` }>
                                <button type="button" className="secondary" onClick={() => handleRemoveLocation(index)}>장소 삭제</button>
                              </div>
                            </div>
                          ))}
                      </div>
                    </div>
                    {/* 이메일 */}
                    <div className="formRow fullWidth">
                      <div className="labelBox">
                        <label htmlFor="email">이메일</label>
                      </div>
                      <div className={`fieldBox ${ emailError }`}>
                        <input type="text" name="email" id="email" value={contactData.email} ref={ emailRef } onChange={ handleChange } placeholder="이메일" />
                        <div className="fieldError">
                          <p>{ emailErrorText }</p>
                        </div>
                      </div>
                    </div>
                    {/* SNS */}
                    <div className="formRow fullWidth">
                      <div className="labelBox">
                        <label>SNS 연결</label>
                      </div>
                      <div className="fieldBox">
                        <div className={styleClasses.snsGroup}>
                          {Object.keys(contactData.sns).map((platform) => (
                            <div key={platform} className={styleClasses.snsInputRow}>
                              <div className={`labelBox ${ styleClasses.customLabelBox }`}>
                                <label htmlFor={`platform${ platform }`}>
                                  {platform.charAt(0).toUpperCase() + platform.slice(1)}
                                </label>
                              </div>
                              <div className={`fieldBox ${ styleClasses.customFieldBox }`}>
                                <input
                                  type="text"
                                  name={platform}
                                  id={`platform${ platform }`}
                                  value={contactData.sns[platform] || ""}
                                  placeholder={`Enter ${platform} URL`}
                                  onChange={(e) =>
                                    handleSNSChange(platform, e.target.value)
                                  }
                                />
                              </div>
                              <div className={`buttonBox ${ styleClasses.customButtonBox }`}>
                                <div className="commonButton fullWidth">
                                  <button
                                    type="button"
                                    className="secondary"
                                    onClick={() => handleRemoveSNS(platform)}
                                  >
                                    삭제
                                  </button>
                                </div>
                              </div>
                            </div>
                          ))}
                          <div className={styleClasses.addSnsRow}>
                            <div className={`labelBox ${ styleClasses.customLabelBox }`}>
                              <label htmlFor="newSNSPlaform">SNS 링크 선택</label>
                            </div>
                            <div className={`fieldBox ${ styleClasses.customFieldBox }`}>
                              <select id="newSNSPlaform" value={newSNSPlatform} onChange={(e) => setNewSNSPlatform(e.target.value)}>
                                <option value="">SNS 플랫폼</option>
                                {remainingSNSPlatforms.map((platform) => (
                                  <option key={platform} value={platform}>
                                    {platform}
                                  </option>
                                ))}
                              </select>
                            </div>
                            <div className={`buttonBox ${ styleClasses.customButtonBox }`}>
                              <div className="commonButton fullWidth">
                                <button type="button" onClick={handleAddSNS}>
                                  추가
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    {/* 저장 및 삭제 버튼 */}
                    <div className={ styleClasses.buttonWrap }>
                      <div className={ styleClasses.topButtons }>
                        <div className="commonButton fullwidth">
                          <Link to={`/my-page/contact`} className='secondary'>목록</Link>
                        </div>
                        <div className="commonButton fullWidth">
                        <button className="black" onClick={ handleSave }>
                          저장하기
                          <div className={`buttonLoading ${ buttonLoading ? 'active' : undefined }`}>
                            <div></div>
                            <div></div>
                            <div></div>
                          </div>
                        </button>
                        </div>
                      </div>
                    </div>
                    </>
                  )
                }
              </div>
            </div>
          </div>
        </div>
      </div>
      <Popup open={ openPopup } setOpenPopup={ setOpenPopup } title={ popupTitle } buttonText={ popupButtonText } buttonFunction={ customFunction } />
    </>
  );
}

export default ContactDetail;